/**
 * Custom hook for managing validation of Question Labels
 */
import { useState } from "react"
import { LabelType } from "utils/labelUtils"

/**
 * Validates Question Label format
 */
export const validateQuestionLabel = (
  content: string
): { isValid: boolean; error?: string } => {
  const trimmedContent = content.trim()

  // Check if it starts with Q
  if (!trimmedContent.startsWith("Q ")) {
    return { isValid: false, error: 'Question label must start with "Q "' }
  }

  // Check if it has a colon
  if (!trimmedContent.includes(":")) {
    return {
      isValid: false,
      error: 'Question label must contain a colon ":"',
    }
  }

  // Extract label between Q and :
  const colonIndex = trimmedContent.indexOf(":")
  const labelPart = trimmedContent.substring(2, colonIndex).trim()

  // Check if there's a label between Q and :
  if (!labelPart || labelPart.length === 0) {
    return {
      isValid: false,
      error: 'Question label must have a label name between "Q" and ":"',
    }
  }

  // Check if there's text after the colon
  const textAfterColon = trimmedContent.substring(colonIndex + 1).trim()
  if (!textAfterColon || textAfterColon.length === 0) {
    return {
      isValid: false,
      error: 'Question label must have question text after the colon ":"',
    }
  }

  return { isValid: true }
}

export const useValidation = () => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})

  /**
   * Validates a label and updates the validation errors state
   */
  const validateLabel = (
    id: string,
    content: string,
    type: LabelType
  ): void => {
    if (type === LabelType.QUESTION) {
      const validation = validateQuestionLabel(content)
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        if (!validation.isValid) {
          newErrors[id] = validation.error!
        } else {
          delete newErrors[id]
        }
        return newErrors
      })
    }
  }

  return {
    validationErrors,
    validateLabel,
  }
}
